import { Box, Card, CardContent, Chip, Divider, IconButton, Stack, Typography } from '@mui/joy';
import { CaretDown, CaretRight, Trash } from '@phosphor-icons/react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { CacheGroup, CacheImpact } from 'dashboard/enums';
import { ICacheEntry } from 'dashboard/types';
import { Color, Direction, Level, Size, Variant } from 'theme/enums';

interface IProps {
  caches: ICacheEntry[];
  clearingKey: string | null;
  /** The environment the back end answered on; may be empty if it could not be resolved. */
  environment: string;
  group: CacheGroup;
  onClear: (cache: ICacheEntry) => void;
}

const IMPACT_COLOR: Record<CacheImpact, Color> = {
  [CacheImpact.HIGH]: Color.DANGER,
  [CacheImpact.LOW]: Color.NEUTRAL,
  [CacheImpact.MEDIUM]: Color.WARNING,
};

const CARET_SIZE = 16;

export const CacheGroupCard = ({ caches, clearingKey, environment, group, onClear }: IProps) => {
  const { t } = useTranslation();

  // Which rows have their explanation open. Local to the card on purpose: it is a
  // reading aid, nothing else on the page depends on it and it should not survive
  // a refetch of the cache list.
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const toggleDetails = useCallback(
    (key: string) =>
      setExpandedKeys((keys) =>
        keys.includes(key) ? keys.filter((expanded) => expanded !== key) : [...keys, key]
      ),
    []
  );

  if (!caches.length) {
    return null;
  }

  return (
    <Card sx={{ boxShadow: Size.SM }} variant={Variant.OUTLINED}>
      <CardContent>
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            {/* Only this group's reach is a single environment, so only its heading names
                which one. A separate key rather than an appended string: with no
                environment resolved, the interpolated title would read "only ()". */}
            <Typography level={Level.TITLE_MD}>
              {CacheGroup.FILE === group && !!environment
                ? t('titleCacheGroupFileWithEnvironment', { environment })
                : t(`titleCacheGroup_${group}`)}
            </Typography>

            <Typography level={Level.BODY_SM} sx={{ color: 'text.tertiary' }}>
              {t(`messageCacheGroupHelp_${group}`)}
            </Typography>
          </Stack>

          <Divider />

          <Stack divider={<Divider />} spacing={1.5}>
            {caches.map((cache) => {
              const label = t(`labelCache_${cache.key}`, cache.key);
              const isExpanded = expandedKeys.includes(cache.key);

              return (
                <Stack key={cache.key} spacing={1}>
                  <Stack
                    alignItems={{ sm: 'center', xs: 'flex-start' }}
                    direction={{ sm: Direction.ROW, xs: Direction.COLUMN }}
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Stack alignItems="center" direction={Direction.ROW} spacing={1}>
                      {/* The explanation is opt-in: an operator who knows the estate
                            should not have to scroll past a paragraph per cache. */}
                      <IconButton
                        aria-expanded={isExpanded}
                        aria-label={t('tooltipCacheDetails', { cache: label })}
                        color={Color.NEUTRAL}
                        onClick={() => toggleDetails(cache.key)}
                        size={Size.SM}
                        variant={Variant.PLAIN}
                      >
                        {isExpanded ? (
                          <CaretDown size={CARET_SIZE} weight="bold" />
                        ) : (
                          <CaretRight size={CARET_SIZE} weight="bold" />
                        )}
                      </IconButton>

                      <Typography level={Level.TITLE_SM}>{label}</Typography>

                      {/* Blast radius is the thing an operator needs before clicking,
                            so it is stated rather than implied by button colour alone. */}
                      <Chip
                        color={IMPACT_COLOR[cache.impact]}
                        size={Size.SM}
                        variant={Variant.SOFT}
                      >
                        {t(`labelCacheImpact_${cache.impact}`)}
                      </Chip>
                    </Stack>

                    <Button
                      color={CacheImpact.HIGH === cache.impact ? Color.DANGER : Color.NEUTRAL}
                      disabled={null !== clearingKey && clearingKey !== cache.key}
                      loading={clearingKey === cache.key}
                      onClick={() => onClear(cache)}
                      size={Size.SM}
                      startDecorator={<Trash />}
                      text={t('buttonLabelClear')}
                      tooltipProps={{
                        children: null,
                        title: t('tooltipClearCache', { cache: label }),
                      }}
                      variant={Variant.OUTLINED}
                    />
                  </Stack>

                  {isExpanded && (
                    <Box
                      sx={{
                        backgroundColor: 'background.level1',
                        borderRadius: Size.SM,
                        padding: 1.5,
                      }}
                    >
                      <Stack spacing={1}>
                        {/* First, not buried in the prose: whether a cache expires on its
                            own decides whether clearing it by hand is worth doing at all,
                            and several of these never do. */}
                        {!!t(`messageCacheExpiry_${cache.key}`, '') && (
                          <Typography level={Level.BODY_XS} sx={{ color: 'text.tertiary' }}>
                            <strong>{t('labelCacheExpiry')}:</strong>{' '}
                            {t(`messageCacheExpiry_${cache.key}`)}
                          </Typography>
                        )}

                        <Typography level={Level.BODY_SM}>
                          {t(`messageCacheDetails_${cache.key}`, '')}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};
